/**
 * Product filtering and sorting utilities.
 *
 * Server-side filters (handled by Saleor GraphQL):
 * - categories: via ProductFilterInput.categories (requires IDs)
 * - price: via ProductFilterInput.price range
 *
 * Client-side filters (handled here):
 * - colors: Saleor doesn't support attribute filtering without IDs
 * - sizes: Same as colors
 *
 * Note: Server-only functions (like resolveCategorySlugsToIds) are in filter-utils.server.ts
 */

import { getProductCategories } from "@/lib/category/catalog-categories";
import { isPurchasableVariant } from "@/lib/packaging/variant-order";
import type { ProductOrder, ProductOrderField, OrderDirection, ProductFilterInput } from "@/gql/graphql";
import { compareSizes } from "@/lib/sizes";
import type { ProductCardData } from "./product-card";
import type { PeptideProductCardData } from "@/ui/components/peptide";
import type { FilterOption, ActiveFilter, SortOption } from "./filter-bar";

export type ProductOrPeptideCard = ProductCardData | PeptideProductCardData;

export type AvailabilityFilter = "all" | "in_stock" | "out_of_stock";

export function normalizeAvailability(value: string | null | undefined): AvailabilityFilter {
	return value === "in_stock" || value === "out_of_stock" ? value : "all";
}

export function normalizeSortOption(value: string | null | undefined): SortOption {
	if (value === "bestselling") return "rating_desc";
	switch (value) {
		case "newest":
		case "price_asc":
		case "price_desc":
		case "rating_desc":
		case "name_asc":
		case "name_desc":
		case "score_desc":
			return value;
		default:
			return "featured";
	}
}

/** Uses the same stock, price and currency rule as the purchase controls. */
function isProductPurchasable(product: ProductOrPeptideCard): boolean {
	return (
		product.variants?.some((variant) =>
			isPurchasableVariant({
				quantityAvailable: variant.quantityAvailable,
				pricing: {
					price:
						variant.price == null || !variant.currency
							? null
							: {
									gross: { amount: variant.price, currency: variant.currency },
								},
				},
			}),
		) ?? false
	);
}

function hasPeptideMetadata(p: ProductOrPeptideCard): p is PeptideProductCardData {
	return "peptideMetadata" in p && p.peptideMetadata != null;
}

// ============================================================================
// Types
// ============================================================================

export interface CategoryOption {
	id: string;
	name: string;
	slug: string;
	count: number;
}

// ============================================================================
// Static Price Ranges (for server-side filtering)
// ============================================================================

const CHANNEL_CURRENCY_SYMBOL: Record<string, string> = { standard: "€" };

export function getCurrencySymbol(channel: string): string {
	return CHANNEL_CURRENCY_SYMBOL[channel.toLowerCase()] ?? "$";
}

export function getStaticPriceRanges(channel: string) {
	const sym = getCurrencySymbol(channel);
	return [
		{ label: `Under ${sym}100`, value: "0-100" },
		{ label: `${sym}100 - ${sym}250`, value: "100-250" },
		{ label: `${sym}250 - ${sym}500`, value: "250-500" },
		{ label: `${sym}500 - ${sym}1000`, value: "500-1000" },
		{ label: `${sym}1000+`, value: "1000-" },
	];
}

export function getStaticPriceRangesWithCount(channel: string) {
	return getStaticPriceRanges(channel).map((r) => ({ ...r, count: 0 }));
}

/** @deprecated Use getStaticPriceRanges(channel) for channel-aware labels */
export const STATIC_PRICE_RANGES = [
	{ label: "Under $50", value: "0-50" },
	{ label: "$50 - $100", value: "50-100" },
	{ label: "$100 - $200", value: "100-200" },
	{ label: "$200+", value: "200-" },
] as const;

/** @deprecated Use getStaticPriceRangesWithCount(channel) for channel-aware labels */
export const STATIC_PRICE_RANGES_WITH_COUNT = STATIC_PRICE_RANGES.map((r) => ({ ...r, count: 0 }));

// ============================================================================
// Server-side: Saleor GraphQL Filters
// ============================================================================

/**
 * Build Saleor ProductFilterInput from URL params.
 */
export function buildFilterVariables(params: {
	priceRange?: string | null;
	categoryIds?: string[];
}): ProductFilterInput | undefined {
	const filter: ProductFilterInput = {};
	let hasFilter = false;

	if (params.categoryIds?.length) {
		filter.categories = params.categoryIds;
		hasFilter = true;
	}

	if (params.priceRange) {
		const [minStr, maxStr] = params.priceRange.split("-");
		const min = parseFloat(minStr) || 0;
		const max = maxStr ? parseFloat(maxStr) : undefined;
		filter.price = { gte: min, ...(max && { lte: max }) };
		hasFilter = true;
	}

	return hasFilter ? filter : undefined;
}

/**
 * Build Saleor ProductOrder from sort option.
 */
export function buildSortVariables(sort: SortOption | string | undefined): ProductOrder | undefined {
	const normalizedSort = normalizeSortOption(sort);
	if (normalizedSort === "featured") return undefined;

	const sortMap: Record<string, { field: ProductOrderField; direction: OrderDirection }> = {
		newest: { field: "DATE" as ProductOrderField, direction: "DESC" as OrderDirection },
		price_asc: { field: "PRICE" as ProductOrderField, direction: "ASC" as OrderDirection },
		price_desc: { field: "PRICE" as ProductOrderField, direction: "DESC" as OrderDirection },
		rating_desc: { field: "RATING" as ProductOrderField, direction: "DESC" as OrderDirection },
	};

	return sortMap[normalizedSort];
}

// ============================================================================
// Client-side: Extract Filter Options from Products
// ============================================================================

/**
 * Extract unique category options from products with counts.
 */
export function extractCategoryOptions(products: ProductCardData[]): CategoryOption[] {
	const map = new Map<string, CategoryOption>();

	for (const product of products) {
		// Skip Saleor's auto-created "Default Category" — kein sinnvoller Filter
		if (product.category && product.category.slug !== "default-category") {
			const existing = map.get(product.category.slug);
			if (existing) {
				existing.count++;
			} else {
				map.set(product.category.slug, {
					id: product.category.id,
					name: product.category.translation?.name ?? product.category.name,
					slug: product.category.slug,
					count: 1,
				});
			}
		}
	}

	return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

/**
 * Extract unique color options from products.
 * Selected colors are always included (with count 0) so users can deselect them.
 */
export function extractColorOptions(products: ProductCardData[], selectedColors?: string[]): FilterOption[] {
	const map = new Map<string, { count: number; hex?: string }>();

	for (const product of products) {
		product.colors?.forEach((color) => {
			const existing = map.get(color.name);
			if (existing) {
				existing.count++;
			} else {
				map.set(color.name, { count: 1, hex: color.hex });
			}
		});
	}

	// Ensure selected colors are always shown
	selectedColors?.forEach((color) => {
		if (!map.has(color)) map.set(color, { count: 0 });
	});

	return Array.from(map.entries())
		.map(([name, { count, hex }]) => ({ name, count, hex }))
		.sort((a, b) => b.count - a.count);
}

/**
 * Extract unique size options from products.
 * Selected sizes are always included (with count 0) so users can deselect them.
 */
export function extractSizeOptions(products: ProductCardData[], selectedSizes?: string[]): FilterOption[] {
	const map = new Map<string, number>();

	for (const product of products) {
		product.sizes?.forEach((size) => {
			map.set(size, (map.get(size) || 0) + 1);
		});
	}

	// Ensure selected sizes are always shown
	selectedSizes?.forEach((size) => {
		if (!map.has(size)) map.set(size, 0);
	});

	// Sort sizes in logical order
	return Array.from(map.entries())
		.map(([name, count]) => ({ name, count }))
		.sort((a, b) => compareSizes(a.name, b.name));
}

// ============================================================================
// Client-side: Apply Filters & Sort
// ============================================================================

/**
 * Filter products by colors and sizes (client-side).
 * Categories and price are filtered server-side via GraphQL.
 */
export function filterProducts(
	products: ProductCardData[],
	filters: { colors?: string[]; sizes?: string[] },
): ProductCardData[] {
	let filtered = products;

	if (filters.colors?.length) {
		filtered = filtered.filter((p) => p.colors?.some((c) => filters.colors!.includes(c.name)));
	}

	if (filters.sizes?.length) {
		filtered = filtered.filter((p) => p.sizes?.some((s) => filters.sizes!.includes(s)));
	}

	return filtered;
}

/**
 * Extract peptide category options from products with peptide metadata.
 */
export function extractPeptideCategoryOptions(
	products: ProductOrPeptideCard[],
): { name: string; count: number }[] {
	const map = new Map<string, number>();
	for (const p of products) {
		getProductCategories(
			p.slug,
			hasPeptideMetadata(p) ? p.peptideMetadata?.peptideCategories : undefined,
			p.category,
		).forEach((c) => {
			map.set(c, (map.get(c) ?? 0) + 1);
		});
	}
	return Array.from(map.entries())
		.map(([name, count]) => ({ name, count }))
		.sort((a, b) => b.count - a.count);
}

/**
 * Extract research level options from peptide products.
 */
export function extractResearchLevelOptions(products: ProductOrPeptideCard[]): string[] {
	const set = new Set<string>();
	for (const p of products) {
		if (hasPeptideMetadata(p) && p.peptideMetadata?.researchLevel) {
			set.add(p.peptideMetadata.researchLevel);
		}
	}
	return Array.from(set).sort();
}

/**
 * Extract purchase availability from variants, independently of research metadata.
 */
export function extractAvailabilityOptions(products: ProductOrPeptideCard[]): string[] {
	const options = new Set<string>(
		products.map((product) => (isProductPurchasable(product) ? "in_stock" : "out_of_stock")),
	);
	return ["in_stock", "out_of_stock"].filter((option) => options.has(option));
}

/**
 * Filter products by search, peptide categories, research level, availability.
 */
export function filterProductsByPeptide(
	products: ProductOrPeptideCard[],
	filters: {
		searchQuery?: string;
		peptideCategories?: string[];
		researchLevel?: string;
		availability?: string;
	},
): ProductOrPeptideCard[] {
	let filtered = products;

	const q = filters.searchQuery?.toLowerCase().trim();
	if (q) {
		filtered = filtered.filter((p) => {
			const matchesName = p.name.toLowerCase().includes(q);
			const matchesAliases =
				hasPeptideMetadata(p) &&
				p.peptideMetadata != null &&
				p.peptideMetadata.aliases.some((a) => a.toLowerCase().includes(q));
			const matchesDesc = p.shortDescription?.toLowerCase().includes(q);
			return matchesName || !!matchesAliases || !!matchesDesc;
		});
	}

	if (filters.peptideCategories?.length) {
		filtered = filtered.filter((p) => {
			const categories = getProductCategories(
				p.slug,
				hasPeptideMetadata(p) ? p.peptideMetadata?.peptideCategories : undefined,
				p.category,
			);
			return filters.peptideCategories!.some((c) => categories.includes(c));
		});
	}

	if (filters.researchLevel && filters.researchLevel !== "all") {
		filtered = filtered.filter((p) => {
			if (!hasPeptideMetadata(p) || !p.peptideMetadata) return false;
			return p.peptideMetadata.researchLevel === filters.researchLevel;
		});
	}

	const availability = normalizeAvailability(filters.availability);
	if (availability !== "all") {
		filtered = filtered.filter((product) => isProductPurchasable(product) === (availability === "in_stock"));
	}

	return filtered;
}

/**
 * Sort products client-side (fallback when server sort not applied).
 */
export function sortProductsClientSide<
	T extends {
		price: number;
		createdAt?: string | null;
		reviewSummary?: { avgRating: number; count: number } | null;
	},
>(products: T[], sort: SortOption | string): T[] {
	const sorted = [...products];

	switch (normalizeSortOption(sort)) {
		case "rating_desc":
			return sorted.sort((a, b) => {
				const rating = (product: T) =>
					product.reviewSummary && product.reviewSummary.count > 0 ? product.reviewSummary.avgRating : 0;
				return rating(b) - rating(a);
			});
		case "price_asc":
			return sorted.sort((a, b) => a.price - b.price);
		case "price_desc":
			return sorted.sort((a, b) => b.price - a.price);
		case "newest":
			return sorted.sort((a, b) => {
				const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
				const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
				return dateB - dateA;
			});
		case "name_asc":
			return sorted.sort((a, b) => {
				const nameA =
					"name" in a && typeof (a as { name?: string }).name === "string"
						? (a as { name: string }).name
						: "";
				const nameB =
					"name" in b && typeof (b as { name?: string }).name === "string"
						? (b as { name: string }).name
						: "";
				return nameA.localeCompare(nameB);
			});
		case "name_desc":
			return sorted.sort((a, b) => {
				const nameA =
					"name" in a && typeof (a as { name?: string }).name === "string"
						? (a as { name: string }).name
						: "";
				const nameB =
					"name" in b && typeof (b as { name?: string }).name === "string"
						? (b as { name: string }).name
						: "";
				return nameB.localeCompare(nameA);
			});
		case "score_desc":
			return sorted.sort((a, b) => {
				const getScore = (x: T): number => {
					if (!("peptideMetadata" in x)) return 0;
					const pm = (x as Record<string, unknown>).peptideMetadata as
						{ scores?: { overall?: number } } | undefined;
					const score = pm?.scores?.overall;
					return typeof score === "number" ? score : 0;
				};
				return getScore(b) - getScore(a);
			});
		default:
			return sorted;
	}
}

// ============================================================================
// Active Filters Display
// ============================================================================

/**
 * Build active filters array for display.
 * Note: Categories are added separately from resolved server data.
 */
export function buildActiveFilters(filters: {
	colors?: string[];
	sizes?: string[];
	priceRange?: string | null;
	currencySymbol?: string;
}): ActiveFilter[] {
	const active: ActiveFilter[] = [];
	const sym = filters.currencySymbol ?? "$";

	filters.colors?.forEach((color) => {
		active.push({ key: "color", label: "Color", value: color });
	});

	filters.sizes?.forEach((size) => {
		active.push({ key: "size", label: "Size", value: size });
	});

	if (filters.priceRange) {
		const [min, max] = filters.priceRange.split("-");
		const label = max ? `${sym}${min} - ${sym}${max}` : `${sym}${min}+`;
		active.push({ key: "price", label: "Price", value: label });
	}

	return active;
}
